import { useEffect, useRef } from 'react'
import { useAppToast } from '@ui-seeker/app'
import { getSubmit, postSubmit } from '@ui-seeker/utils'
import { BACKEND_APIS } from '../../../@constants/routes'
import {
    tableData,
    summaryData,
    isLoadingTable,
    filterDateFrom,
    filterDateTo,
    excludePropagated,
    userActivityData,
    userStatsData,
    isLoadingUserDetail,
    selectedUser,
    applyQuickFilter
} from '../@signals'

export default function useUserStats() {
    const { showToast } = useAppToast()
    const initializedRef = useRef(false)

    // Fetch all users stats
    const fetchUserStats = (showLoading = true) => {
        if (showLoading) {
            isLoadingTable.value = true
        }

        const filters = {
            date_from: filterDateFrom.value || undefined,
            date_to: filterDateTo.value || undefined,
            exclude_propagated: excludePropagated.value
        }

        // Remove undefined values
        const cleanFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, v]) => v !== undefined)
        )

        postSubmit(`${BACKEND_APIS.AUDIT_LOG}/all-users-stats`, cleanFilters)
            .then(res => {
                if (res) {
                    tableData.value = res.data || []
                    summaryData.value = res.summary || null
                }
            })
            .catch(err => {
                console.error('Error fetching user stats:', err)
                showToast.error('Error loading user statistics')
            })
            .finally(() => {
                isLoadingTable.value = false
            })
    }

    // Fetch user detail (stats + activity)
    const fetchUserDetail = (userId, days = 30) => {
        isLoadingUserDetail.value = true

        // For System user (null), we can't use the existing endpoints
        // We need to get data from the table
        if (userId === null) {
            // Get data from tableData for System user
            const systemUser = tableData.value.find(u => u.is_system)
            if (systemUser) {
                userStatsData.value = {
                    user_id: null,
                    user_email: 'System',
                    total_mappings: systemUser.total_actions,
                    daily_average: systemUser.rate_per_hour,
                    by_action: {
                        MAP: systemUser.map_count,
                        REMAP: systemUser.remap_count,
                        UNMAP: systemUser.unmap_count
                    },
                    by_source: systemUser.by_source
                }
                userActivityData.value = []
            }
            isLoadingUserDetail.value = false
            return
        }

        Promise.all([
            getSubmit(`${BACKEND_APIS.AUDIT_LOG}/user/${userId}/stats`, { days }),
            getSubmit(`${BACKEND_APIS.AUDIT_LOG}/user/${userId}/activity`, { days })
        ])
            .then(([stats, activity]) => {
                userStatsData.value = stats
                userActivityData.value = activity?.activity || []
            })
            .catch(err => {
                console.error('Error fetching user detail:', err)
                showToast.error('Error loading user details')
            })
            .finally(() => {
                isLoadingUserDetail.value = false
            })
    }

    // Initial load - only once
    useEffect(() => {
        if (initializedRef.current) return
        initializedRef.current = true

        // Apply default quick filter (this month)
        applyQuickFilter('thisMonth')
        fetchUserStats()
    }, [])

    // Watch for user selection changes
    useEffect(() => {
        if (selectedUser.value !== null) {
            const userId = selectedUser.value.user_id
            fetchUserDetail(userId)
        }
    }, [selectedUser.value])

    return {
        fetchUserStats,
        fetchUserDetail
    }
}
